/**
 * Boot profiler — instrumentación pasiva del pipeline de carga de Map V2.
 *
 * Objetivo: medir reproducibly el tiempo de cada fase (HTTP fetch, normalize,
 * MapLibre load, icon register, first flush, first interactive) sin necesidad
 * de exportar traces de Performance panel.
 *
 * Activación:
 *   - DEV mode (import.meta.env.DEV) → ON automático.
 *   - localStorage 'kalan:profile-boot' === '1' → ON.
 *   - Sin flag: las marks se siguen creando (cost ~µs) pero NO se imprime
 *     reporte ni se calculan snapshots de health.
 *
 * Atajos:
 *   - Ctrl+Shift+B → copia el JSON del último __bootProfile al clipboard.
 *
 * API:
 *   profiler.mark(name)
 *   profiler.measure(name, startMark, endMark)
 *   profiler.span(name, fn)         // sync o async; envuelve mark+mark+measure
 *   profiler.count(bucket, valueMs) // histograma simple
 *   profiler.recordWsMessage(durMs) // distribución de duraciones del WS handler
 *   profiler.snapshotHealth()       // captura heap/nodes/listeners/monitors
 *   profiler.report()               // imprime resumen + copia JSON
 *   profiler.bootDone()             // marca fin del boot, dispara reporte si ON
 *   profiler.recordEntityCount(type, n)
 *
 * Persistencia: el último profile queda en window.__bootProfile y en
 * localStorage 'kalan:boot-profile-last' para sobrevivir recargas.
 */

var ENABLED = (function() {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) return true
  } catch (_) {}
  try {
    if (window.localStorage && window.localStorage.getItem('kalan:profile-boot') === '1') return true
  } catch (_) {}
  return false
})()

var startTs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0
var bootDoneCalled = false

// Limpiamos marks/measures previas con prefijo boot.* para evitar acumulación
// entre HMR reloads (Vite mantiene el window vivo entre cambios).
;(function clearStaleBootMarks() {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) return
  try {
    performance.getEntriesByType('mark').forEach(function(e) {
      if (e.name.indexOf('boot.') === 0 || e.name.indexOf('interact.') === 0) {
        if (performance.clearMarks) performance.clearMarks(e.name)
      }
    })
    performance.getEntriesByType('measure').forEach(function(e) {
      if (e.name.indexOf('boot.') === 0 || e.name.indexOf('interact.') === 0) {
        if (performance.clearMeasures) performance.clearMeasures(e.name)
      }
    })
  } catch (_) {}
})()

var profile = {
  startTs: startTs,
  env: captureEnv(),
  phases: [],          // [{name, startMs, durMs}]
  counts: {
    byEntityType: {},  // {camera: 7206, ...}
    totalMonitors: 0,
    longTasksGT50ms: { count: 0, totalMs: 0 },
    longTasksGT200ms: { count: 0, totalMs: 0 },
  },
  wsBootStats: {
    messageCount: 0,
    totalMs: 0,
    maxMs: 0,
    buckets: { '<1ms': 0, '1-10ms': 0, '10-100ms': 0, '100-500ms': 0, '>500ms': 0 },
  },
  healthAfterBoot: null,
  custom: {},
}
window.__bootProfile = profile

// PerformanceObserver para longtasks (>50ms) durante el boot.
// Se desactiva al primer bootDone() para no acumular indefinidamente.
var longTaskObserver = null
;(function startLongTaskObserver() {
  if (typeof PerformanceObserver === 'undefined') return
  try {
    longTaskObserver = new PerformanceObserver(function(list) {
      var entries = list.getEntries()
      for (var i = 0; i < entries.length; i++) {
        var dur = entries[i].duration
        profile.counts.longTasksGT50ms.count++
        profile.counts.longTasksGT50ms.totalMs += dur
        if (dur > 200) {
          profile.counts.longTasksGT200ms.count++
          profile.counts.longTasksGT200ms.totalMs += dur
        }
      }
    })
    longTaskObserver.observe({ entryTypes: ['longtask'] })
  } catch (_) { longTaskObserver = null }
})()

function captureEnv() {
  var env = {
    userAgent: navigator.userAgent,
    deviceMemoryGB: navigator.deviceMemory || null,
    cpuCores: navigator.hardwareConcurrency || null,
    devicePixelRatio: window.devicePixelRatio || 1,
    viewport: window.innerWidth + 'x' + window.innerHeight,
    enabled: ENABLED,
    timestamp: new Date().toISOString(),
  }
  try {
    if (navigator.connection) {
      env.connection = {
        effectiveType: navigator.connection.effectiveType,
        downlinkMbps: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
      }
    }
  } catch (_) {}
  return env
}

export function mark(name) {
  if (typeof performance === 'undefined' || !performance.mark) return
  try { performance.mark(name) } catch (_) {}
}

export function measure(name, startMark, endMark) {
  if (typeof performance === 'undefined' || !performance.measure) return null
  try {
    var m = performance.measure(name, startMark, endMark)
    // Algunas implementaciones devuelven undefined; recuperar via getEntriesByName.
    if (!m) {
      var entries = performance.getEntriesByName(name, 'measure')
      m = entries[entries.length - 1]
    }
    if (m) {
      profile.phases.push({
        name: name,
        startMs: Math.round(m.startTime - startTs),
        durMs: Math.round(m.duration),
      })
    }
    return m
  } catch (_) { return null }
}

export function span(name, fn) {
  var startName = name + '.start'
  var endName = name + '.end'
  mark(startName)
  var done = function() { mark(endName); measure(name, startName, endName) }
  try {
    var r = fn()
    if (r && typeof r.then === 'function') {
      return r.then(function(v) { done(); return v }, function(e) { done(); throw e })
    }
    done()
    return r
  } catch (e) {
    done()
    throw e
  }
}

export function count(bucket, valueMs) {
  if (!profile.custom[bucket]) profile.custom[bucket] = { count: 0, totalMs: 0 }
  profile.custom[bucket].count++
  if (typeof valueMs === 'number') profile.custom[bucket].totalMs += valueMs
}

export function recordWsMessage(durMs) {
  if (bootDoneCalled) return  // solo durante el boot
  var s = profile.wsBootStats
  s.messageCount++
  s.totalMs += durMs
  if (durMs > s.maxMs) s.maxMs = durMs
  if (durMs < 1) s.buckets['<1ms']++
  else if (durMs < 10) s.buckets['1-10ms']++
  else if (durMs < 100) s.buckets['10-100ms']++
  else if (durMs < 500) s.buckets['100-500ms']++
  else s.buckets['>500ms']++
}

export function recordEntityCount(entityType, n) {
  profile.counts.byEntityType[entityType] = n
  // recompute total
  var total = 0
  for (var k in profile.counts.byEntityType) total += profile.counts.byEntityType[k]
  profile.counts.totalMonitors = total
}

export function snapshotHealth() {
  if (!ENABLED) return null
  var h = {
    heapUsedMB: null,
    heapLimitMB: null,
    domNodes: null,
    iframes: null,
    jsEventListeners: null,
    monitorsInMemory: null,
    animationsActive: null,
  }
  try {
    if (performance.memory) {
      h.heapUsedMB = Math.round(performance.memory.usedJSHeapSize / 1048576)
      h.heapLimitMB = Math.round(performance.memory.jsHeapSizeLimit / 1048576)
    }
  } catch (_) {}
  try { h.domNodes = document.querySelectorAll('*').length } catch (_) {}
  try { h.iframes = document.querySelectorAll('iframe').length } catch (_) {}
  // jsEventListeners no es accesible vía JS estándar — solo via DevTools
  // (getEventListeners). Lo dejamos null; el spy ad-hoc del usuario puede
  // capturarlo si lo necesita.
  try {
    if (window.__mapV2Monitors) {
      h.monitorsInMemory = Object.keys(window.__mapV2Monitors).length
    }
  } catch (_) {}
  try {
    if (window.__animMetrics) h.animationsActive = window.__animMetrics.activeAnimations || 0
  } catch (_) {}
  profile.healthAfterBoot = h
  return h
}

function buildHumanSummary() {
  var lines = []
  var p = profile
  var phasesByName = {}
  for (var i = 0; i < p.phases.length; i++) phasesByName[p.phases[i].name] = p.phases[i]
  function dur(n) { return phasesByName[n] ? phasesByName[n].durMs + 'ms' : 'n/a' }
  var totalMon = p.counts.totalMonitors || 0

  lines.push('[BootProfile] boot.complete = ' + dur('boot.complete') + ' (' + totalMon + ' monitors)')
  lines.push('  fetch.bus=' + dur('boot.fetch.bus') + ' / fetch.cameras=' + dur('boot.fetch.cameras') + ' / fetch.incidents=' + dur('boot.fetch.incidents') + ' / fetch.devices=' + dur('boot.fetch.devices'))
  lines.push('  normalize.bus=' + dur('boot.normalize.bus') + ' / normalize.cameras=' + dur('boot.normalize.cameras') + ' / normalize.devices=' + dur('boot.normalize.devices'))
  lines.push('  maplibre-load=' + dur('boot.maplibre-load') + ' / layers-add=' + dur('boot.layers-add') + ' / icons-register=' + dur('boot.icons-register'))
  lines.push('  first-flush=' + dur('boot.first-flush') + ' / first-interactive=' + dur('boot.first-interactive'))
  var lt50 = p.counts.longTasksGT50ms
  var lt200 = p.counts.longTasksGT200ms
  lines.push('  longtasks: >50ms count=' + lt50.count + ' totalMs=' + Math.round(lt50.totalMs)
    + ' | >200ms count=' + lt200.count + ' totalMs=' + Math.round(lt200.totalMs))
  if (p.healthAfterBoot) {
    var h = p.healthAfterBoot
    lines.push('  health: heap=' + h.heapUsedMB + 'MB nodes=' + h.domNodes + ' iframes=' + h.iframes + ' monitors=' + h.monitorsInMemory + ' anim=' + h.animationsActive)
  }
  var ws = p.wsBootStats
  if (ws.messageCount > 0) {
    lines.push('  ws msgs=' + ws.messageCount + ' avg=' + (ws.totalMs / ws.messageCount).toFixed(2) + 'ms max=' + ws.maxMs.toFixed(0) + 'ms (>500ms:' + ws.buckets['>500ms'] + ')')
  }
  return lines.join('\n')
}

export function report() {
  // Recolecta phases del Performance API que pudieron no haber sido push'eadas
  // (HMR reloads pueden hacer que el module se re-cargue sin perder marks).
  try {
    var measures = performance.getEntriesByType('measure') || []
    var seen = {}
    profile.phases.forEach(function(p) { seen[p.name] = true })
    measures.forEach(function(m) {
      if ((m.name.indexOf('boot.') === 0 || m.name.indexOf('interact.') === 0) && !seen[m.name]) {
        profile.phases.push({
          name: m.name,
          startMs: Math.round(m.startTime - startTs),
          durMs: Math.round(m.duration),
        })
        seen[m.name] = true
      }
    })
    // Ordenamos por startMs para legibilidad.
    profile.phases.sort(function(a, b) { return a.startMs - b.startMs })
  } catch (_) {}

  if (!ENABLED) {
    console.log('[BootProfile] (disabled — set localStorage.kalan:profile-boot=1 to enable)')
    return profile
  }

  console.log(buildHumanSummary())
  console.table(profile.phases.map(function(p) { return { phase: p.name, startMs: p.startMs, durMs: p.durMs } }))

  try {
    var json = JSON.stringify(profile, null, 2)
    window.localStorage.setItem('kalan:boot-profile-last', json)
  } catch (_) {}

  console.log('[BootProfile] window.__bootProfile = full profile object | __bootProfile.report() to reprint | Ctrl+Shift+B to copy JSON')
  return profile
}

export function bootDone() {
  if (bootDoneCalled) return
  bootDoneCalled = true
  // Mark final + measure de boot.complete (si no se hizo manual).
  mark('boot.complete.end')
  var hasStart = performance.getEntriesByName('boot.complete.start', 'mark').length > 0
  if (hasStart) {
    measure('boot.complete', 'boot.complete.start', 'boot.complete.end')
  } else {
    // Fallback: medir desde startTs del módulo.
    profile.phases.push({
      name: 'boot.complete',
      startMs: 0,
      durMs: Math.round(performance.now() - startTs),
    })
  }
  snapshotHealth()
  // Detener long task observer (acumular indefinidamente no aporta).
  if (longTaskObserver) {
    try { longTaskObserver.disconnect() } catch (_) {}
    longTaskObserver = null
  }
  report()
}

// Atajo Ctrl+Shift+B copia el __bootProfile al clipboard.
;(function setupClipboardShortcut() {
  if (typeof document === 'undefined') return
  document.addEventListener('keydown', function(e) {
    if (e.ctrlKey && e.shiftKey && (e.key === 'B' || e.key === 'b')) {
      e.preventDefault()
      try {
        var json = JSON.stringify(profile, null, 2)
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(json).then(function() {
            console.log('[BootProfile] copiado al clipboard (' + json.length + ' chars)')
          })
        } else {
          // Fallback via textarea
          var ta = document.createElement('textarea')
          ta.value = json
          document.body.appendChild(ta)
          ta.select()
          try { document.execCommand('copy') } catch (_) {}
          document.body.removeChild(ta)
          console.log('[BootProfile] copiado al clipboard (fallback)')
        }
      } catch (_) {}
    }
  })
})()

// Marca de inicio del módulo. Sirve como anchor para measures relativas.
mark('boot.module-loaded')

// Exponer API completa global para que el usuario pueda invocar desde consola.
window.__bootProfile.mark = mark
window.__bootProfile.measure = measure
window.__bootProfile.span = span
window.__bootProfile.count = count
window.__bootProfile.recordWsMessage = recordWsMessage
window.__bootProfile.recordEntityCount = recordEntityCount
window.__bootProfile.snapshotHealth = snapshotHealth
window.__bootProfile.report = report
window.__bootProfile.bootDone = bootDone

console.log('[BootProfile] module loaded (' + (ENABLED ? 'ENABLED' : 'disabled') + ')')
